import type { ReactNode } from 'react';
import React, { useEffect, useRef, useState } from 'react';
import PhoneForwardedIcon from '@mui/icons-material/PhoneForwarded';
import axios from 'axios';
import { Formik } from 'formik';
import { noop } from 'lodash';
import * as Yup from 'yup';

import { ClaimContextProvider, useClaim } from '~/components/ClaimContainer';
import mixpanel from '~/components/CmsMain/mixpanel';
import useCreateCommunication from '~/components/communications/CommunicationCard/useCreateCommunication';
import ForwardCallDialog, {
  type ForwardCallFormValues,
} from '~/components/communications/PhoneCall/components/ForwardCallDialog';
import type { DirectionType } from '~/components/communications/PhoneCall/types';
import IconButton from '~/components/core/Atomic/Buttons/IconButton';
import Tooltip from '~/components/core/Atomic/Tooltip';
import { useMinimizedDialogs } from '~/components/core/MinimizedBar/Context';
import { useCms } from '~/components/hooks/useCms';
import useUnloadWindowEventListener from '~/components/hooks/useUnloadWindowEventListener';
import type { PopulatedClaimWithOrganization } from '~/components/types/claim-types';
import { MIXPANEL_EVENTS } from '~/pocs/mixpanel';
import type { ForwardCallParams } from '~/server_shared/openapi/data-contracts';
import { reportAxiosError } from '~/Utils';
import type { PopulatedClaim } from '~/Utils/populateClaim';

import CommunicationCard from '../../CommunicationCard';
import { useCommunicationCallContext } from '../Context/CommunicationCallContext';

import AddParticipantDialog from './AddParticipantDialog';
import CallInProgressAutoSave from './CallInProgressAutoSave';
import CallInProgressParticipantsStatusUpdate from './CallInProgressParticipantsStatusUpdate';
import NewPhoneCallCommunicationSpecificBody from './NewPhoneCallCommunicationSpecificBody';
import PhoneCallCommunicationAction from './PhoneCallCommunicationAction';

interface CallInProgressCardInnerProps {
  isIncoming: boolean;
  onCallHandledAndFinished: () => void;
  openMinimizedDialog: () => void;
  additionalCardActions: ReactNode;
  onMinimized?: () => void;
  disableMinimized?: boolean;
  open?: boolean;
  shouldShowClive?: boolean;
}

interface CallInProgressCardProps {
  isIncoming: boolean;
  onCallHandledAndFinished: () => void;
  shouldShowClive?: boolean;
}

interface PartialFormikValuesProps {
  content: string;
  summary: string;
  exposure_ids: number[];
  claim_id: string;
  is_no_answer: boolean;
  direction: DirectionType;
}

const CallInProgressCardInner: React.FC<CallInProgressCardInnerProps> = (props) => {
  const {
    isIncoming,
    open,
    openMinimizedDialog,
    additionalCardActions,
    onMinimized,
    disableMinimized,
    onCallHandledAndFinished,
    shouldShowClive = false,
  } = props;
  const {
    isMuted,
    isCallActive,
    communication,
    callStatus,
    currentlyClickedDigits,
    callDurationSec,
    handleSendDigit,
    updateCommunication,
    updateMute,
    handleHangup,
  } = useCommunicationCallContext();

  const { userReloadTwilioDetails } = useCms();

  const { communicationCommonInitialValues } = useCreateCommunication('phone_call', {
    exposure_ids: communication.exposure_ids,
    summary: communication.summary,
  });
  const [wereAdditionalParticipantsAdded, setWereAdditionalParticipantsAdded] = useState(false);
  const [participantsDetails, setParticipantsDetails] = useState(communication.additional_conference_participants);
  const [showAddParticipant, setShowAddParticipant] = useState(false);
  const [showForwardCallDialog, setShowForwardCallDialog] = useState(false);
  const [isUpdatingHoldStatus, setIsUpdatingHoldStatus] = useState(false);
  const [isOnHold, setIsOnHold] = useState(false);
  const { setDisableWarn } = useUnloadWindowEventListener();
  const direction = isIncoming ? 'Incoming' : 'Outgoing';

  const initialValues = {
    ...communicationCommonInitialValues,
    claim_id: communication.claim_id || '',
    is_no_answer: false,
    content: communication.content || '',
    summary: communication.summary || '',
    direction,
    is_recording_allowed: !!communication.recording_status, // Best effort when attaching to claim the card is reinitialized
    category_id: communication.categories?.[0]?.id ?? null,
  };

  const forwardCall = communication.direction === 'Incoming' && isCallActive && (
    <Tooltip title="Forward Call">
      <IconButton color="primary" onClick={() => setShowForwardCallDialog(true)}>
        <PhoneForwardedIcon />
      </IconButton>
    </Tooltip>
  );

  const handleClickPutOnHold = async () => {
    try {
      setIsUpdatingHoldStatus(true);
      await axios.post(
        `/api/v1/calls/${communication.id}/${isOnHold ? 'remove_participants_from_hold' : 'put_participants_on_hold'}`
      );
      setIsOnHold(!isOnHold);
    } catch (error) {
      await reportAxiosError(error);
    }
    setIsUpdatingHoldStatus(false);
  };

  const handleDismissed = () => {
    setDisableWarn(true);
    onCallHandledAndFinished();
    userReloadTwilioDetails();
  };

  const handleAddParticipant = async (values: PartialFormikValuesProps) => {
    try {
      await axios.post(`/api/v1/calls/${communication.id}/add_conference_participant`, values);
      const result = await axios.get(`/api/v1/communications/${communication.id}`);
      setParticipantsDetails(result.data.additional_conference_participants);
      setWereAdditionalParticipantsAdded(true);
      setShowAddParticipant(false);
    } catch (error) {
      await reportAxiosError(error);
    }
  };

  const handleForwardCall = async (values: ForwardCallFormValues) => {
    try {
      const payload: ForwardCallParams = {
        forward_to_type: values.forward_to_type,
        adjuster_id: values.adjuster_id,
        queue_name: values.queue_name,
        phone_number: values.phone_number,
        contact_id: values.contact_id,
        contact_phone_id: values.contact_phone_id,
      };

      await axios.post(`/api/v1/calls/${communication.id}/forward_call`, payload);
    } catch (error) {
      await reportAxiosError(error);
      throw error;
    }
  };

  // Open the "Big" Dialog when the call is finished, the user may minimize again
  useEffect(() => {
    if (!isCallActive) {
      openMinimizedDialog();
    }
  }, [isCallActive, openMinimizedDialog]);

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={Yup.object().shape({
        content: Yup.string().required('Required'),
        claim_id: Yup.number().required('Claim must be attached'),
        summary: Yup.string(),
      })}
      onSubmit={async (values, formikProps) => {
        try {
          await axios.patch(`/api/v1/communications/${communication.id}`, {
            content: values.content,
            summary: values.summary,
            exposure_ids: values.exposure_ids,
            category_id: values.category_id,
          });

          onCallHandledAndFinished();
          formikProps.setSubmitting(false);
        } catch (error) {
          formikProps.setSubmitting(false);
          await reportAxiosError(error);
        }
      }}
    >
      {(formikProps) => {
        return (
          <>
            <CommunicationCard
              open={open}
              communication={communication}
              communicationInitialFields={{
                communicationChannel: 'phone_call',
                communicationContact: communication.contact,
                communicationDirection: direction,
              }}
              communicationHeaderAdditionalProps={{
                callDurationSec,
                isCallActive,
              }}
              communicationIdentifier={communication?.specific_identifier || communication?.extra?.phone_number || ''}
              onMinimized={onMinimized}
              disableMinimized={disableMinimized}
              CommunicationTypeSpecificBody={NewPhoneCallCommunicationSpecificBody}
              communicationTypeSpecificBodyProps={{
                setCommunication: updateCommunication,
                onHangup: handleHangup,
                callStatus,
                onToggleMute: updateMute,
                currentlyClickedDigits,
                onClickDigit: handleSendDigit,
                isMuted,
                callDurationSec,
                onClickAddParticipant: () => setShowAddParticipant(true),
                participantsDetails,
                onClickPutOnHold: handleClickPutOnHold,
                isOnHold,
                isUpdatingHoldStatus,
              }}
              isNew
              isDialog
              disableSoftClose
              CommunicationAction={PhoneCallCommunicationAction}
              communicationActionProps={{
                callStatus,
                isSubmitting: formikProps.isSubmitting,
                onSubmit: () => {
                  formikProps.handleSubmit();
                },
                onDismiss: handleDismissed,
              }}
              additionalCardActions={
                <>
                  {forwardCall}
                  {additionalCardActions}
                </>
              }
              title={`${direction} phone call`}
              newPhoneCommunicationUI // TODO NGTPA-20266: remove this prop when new phone communication UI is fully implemented
              shouldShowClive={shouldShowClive}
            />
            <CallInProgressAutoSave
              values={formikProps.values}
              initialValues={initialValues}
              communication={communication}
            />
            {wereAdditionalParticipantsAdded && isCallActive && (
              <CallInProgressParticipantsStatusUpdate
                communication={communication}
                setParticipantsDetails={setParticipantsDetails}
              />
            )}

            {isCallActive ? (
              <>
                {showAddParticipant ? (
                  <AddParticipantDialog
                    isClaimCommunication={!!communication.claim_id}
                    onSubmit={handleAddParticipant}
                    onCancel={() => setShowAddParticipant(false)}
                    isOnHold={isOnHold}
                  />
                ) : null}

                {showForwardCallDialog ? (
                  <ForwardCallDialog
                    isClaimCommunication={!!communication.claim_id}
                    onSubmit={handleForwardCall}
                    participantsDetails={
                      participantsDetails?.map((p) => p.id)?.filter((id): id is number => id !== undefined) || []
                    }
                    onCancel={() => setShowForwardCallDialog(false)}
                  />
                ) : null}
              </>
            ) : null}
          </>
        );
      }}
    </Formik>
  );
};

interface CallInProgressCardInnerWithClaimContextIfNeededProps extends CallInProgressCardInnerProps {
  claim?: PopulatedClaimWithOrganization;
  onClaimUpdate?: () => Promise<PopulatedClaim | void>;
}

const CallInProgressCardInnerWithClaimContextIfNeeded: React.FC<
  CallInProgressCardInnerWithClaimContextIfNeededProps
> = ({ claim, onClaimUpdate, ...props }) => {
  if (claim && onClaimUpdate) {
    return (
      <ClaimContextProvider refreshData={onClaimUpdate} claim={claim}>
        <CallInProgressCardInner {...props} />
      </ClaimContextProvider>
    );
  }

  return <CallInProgressCardInner {...props} />;
};

const CallInProgressCard: React.FC<CallInProgressCardProps> = (props) => {
  return <MinimizedCallInProgressCard {...props} />;
};

const BarHeader: React.FC<{ isCallActive: boolean; callDurationSec: number }> = ({ isCallActive, callDurationSec }) => {
  const seconds = callDurationSec % 60;
  const secString = seconds >= 10 ? String(seconds) : `0${seconds}`;

  return (
    <>
      {isCallActive ? 'Active Phone Call' : 'Phone Call Ended'}{' '}
      <b>
        {Math.floor(callDurationSec / 60)}:{secString}
      </b>
    </>
  );
};

const MinimizedCallInProgressCard = (props: CallInProgressCardProps) => {
  const { add } = useMinimizedDialogs();
  const updateHeaderRef = useRef<(barHeader: ReactNode) => void>(() => noop);
  const { claim, onClaimUpdate } = useClaim();
  const { callDurationSec, isCallActive } = useCommunicationCallContext();

  useEffect(() => {
    updateHeaderRef.current(<BarHeader isCallActive={isCallActive} callDurationSec={callDurationSec} />);
  }, [callDurationSec, isCallActive]);

  useEffect(() => {
    const type = 'PHONE';
    const { handleCloseDialog, openDialog, updateHeader } = add({
      barHeader: <BarHeader isCallActive={isCallActive} callDurationSec={callDurationSec} />,
      type,
      dialogComponent: CallInProgressCardInnerWithClaimContextIfNeeded,
      dialogProps: {
        ...props,
        claim,
        onClaimUpdate,
        onCallHandledAndFinished: () => {
          handleCloseDialog();
          props.onCallHandledAndFinished();
        },
        onMinimized: () => {
          mixpanel.track(MIXPANEL_EVENTS.MINIMIZED_DIALOG, {
            type,
            isCallActive,
            isIncoming: props.isIncoming,
          });
        },
        openMinimizedDialog: () => openDialog(),
      },
    });
    updateHeaderRef.current = updateHeader;
    // Clean up the dialog when the component unmounts
    return () => {
      handleCloseDialog();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
};

export default CallInProgressCard;
